import React, { useState, useEffect } from 'react';
import {
  Store, CreditCard, Receipt, Clock, CheckCircle, XCircle, AlertTriangle,
  Plus, Edit, Trash2, Eye, EyeOff, Search, RefreshCw, DollarSign,
  ChevronDown, ChevronUp, Mail, Phone, MapPin, Globe, Calendar,
  ArrowLeft, Image, Star, TrendingUp, AlertCircle, FileText, Download,
  Crown, Zap, Check, X, Building2, Upload, Link, ExternalLink, Send, Megaphone
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import { db, subscriptions, payments, invoices, listingTiers, emailTemplates, appSettings, paymentRequests, adminNotifications, supabase } from '../lib/supabase';
import { downloadInvoice, createInvoiceDataFromPayment } from '../lib/invoiceGenerator';

// Tier configuration with benefits
const TIER_CONFIG = {
  free: {
    name: 'Free',
    price_monthly: 0,
    price_yearly: 0,
    color: 'gray',
    icon: Store,
    benefits: [
      { name: 'Basic contact info', included: true },
      { name: 'Business name & phone', included: true },
      { name: 'Logo display', included: false },
      { name: 'Website link', included: false },
      { name: 'Business description', included: false },
      { name: 'Services list', included: false },
      { name: 'Analytics dashboard', included: false },
      { name: 'Priority placement', included: false },
      { name: 'Verified badge', included: false }
    ]
  },
  basic: {
    name: 'Basic',
    price_monthly: 3500,
    price_yearly: 37800,
    color: 'blue',
    icon: Zap,
    benefits: [
      { name: 'Basic contact info', included: true },
      { name: 'Business name & phone', included: true },
      { name: 'Logo display', included: true },
      { name: 'Website link', included: true },
      { name: 'Business description', included: true },
      { name: 'Services list', included: true },
      { name: 'Analytics dashboard', included: true },
      { name: 'Priority placement', included: false },
      { name: 'Verified badge', included: false }
    ]
  },
  premium: {
    name: 'Premium',
    price_monthly: 5000,
    price_yearly: 54000,
    color: 'amber',
    icon: Crown,
    benefits: [
      { name: 'Basic contact info', included: true },
      { name: 'Business name & phone', included: true },
      { name: 'Logo display', included: true },
      { name: 'Website link', included: true },
      { name: 'Business description', included: true },
      { name: 'Services list', included: true },
      { name: 'Analytics dashboard', included: true },
      { name: 'Priority placement', included: true },
      { name: 'Verified badge', included: true }
    ]
  }
};

// Format currency
const formatCurrency = (amount, currency = 'JMD') => {
  return new Intl.NumberFormat('en-JM', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  }).format(amount);
};

// Tier Badge Component
const TierBadge = ({ tier, showPrice = false, billingPeriod = 'monthly' }) => {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.free;
  const Icon = config.icon;
  const price = billingPeriod === 'annual' ? config.price_yearly : config.price_monthly;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-${config.color}-100 text-${config.color}-700`}>
      <Icon className="w-4 h-4" />
      <span className="font-bold">{config.name}</span>
      {showPrice && price > 0 && (
        <span className="text-sm">({formatCurrency(price)}/{billingPeriod === 'annual' ? 'yr' : 'mo'})</span>
      )}
    </div>
  );
};

// Tier Benefits Display
const TierBenefits = ({ tier }) => {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.free;

  return (
    <div className="bg-gray-50 rounded-xl p-4 mt-3">
      <h4 className="font-bold text-sm text-gray-700 mb-2">Tier Benefits</h4>
      <div className="grid grid-cols-2 gap-2">
        {config.benefits.map((benefit, i) => (
          <div key={i} className={`flex items-center gap-2 text-sm ${benefit.included ? 'text-brand-700' : 'text-gray-400'}`}>
            {benefit.included ? (
              <Check className="w-4 h-4 text-brand-500" />
            ) : (
              <X className="w-4 h-4 text-gray-300" />
            )}
            <span>{benefit.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'amber', label: 'Pending Payment' },
    active: { color: 'emerald', label: 'Active' },
    grace_period: { color: 'orange', label: 'Grace Period' },
    suspended: { color: 'red', label: 'Suspended' },
    cancelled: { color: 'gray', label: 'Cancelled' }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${config.color}-100 text-${config.color}-700`}>
      {config.label}
    </span>
  );
};

// Helper to parse tier/logo from additional_info if columns don't exist
const parseAdditionalInfo = (additionalInfo) => {
  if (!additionalInfo) return { tier: null, logoUrl: null, billingPeriod: null };

  const result = { tier: null, logoUrl: null, billingPeriod: null };

  // Parse TIER: PREMIUM or TIER: BASIC
  const tierMatch = additionalInfo.match(/TIER:\s*(PREMIUM|BASIC|FREE)/i);
  if (tierMatch) {
    result.tier = tierMatch[1].toLowerCase();
  }

  // Parse LOGO: https://...
  const logoMatch = additionalInfo.match(/LOGO:\s*(https?:\/\/[^\s|]+)/i);
  if (logoMatch) {
    result.logoUrl = logoMatch[1];
  }

  // Parse BILLING: annual or monthly
  const billingMatch = additionalInfo.match(/BILLING:\s*(annual|monthly)/i);
  if (billingMatch) {
    result.billingPeriod = billingMatch[1].toLowerCase();
  }

  return result;
};

// Get submission tier (from column or parsed from additional_info)
const getSubmissionTier = (sub) => {
  if (sub.tier && sub.tier !== 'free') return sub.tier;
  const parsed = parseAdditionalInfo(sub.additional_info);
  return parsed.tier || sub.tier || 'free';
};

// Get submission logo (from column or parsed from additional_info)
const getSubmissionLogo = (sub) => {
  if (sub.logo_url) return sub.logo_url;
  const parsed = parseAdditionalInfo(sub.additional_info);
  return parsed.logoUrl || null;
};

// Get submission billing period
const getSubmissionBillingPeriod = (sub) => {
  if (sub.billing_period) return sub.billing_period;
  const parsed = parseAdditionalInfo(sub.additional_info);
  return parsed.billingPeriod || 'monthly';
};

// ==================== ENHANCED SUBMISSIONS PAGE ====================
export const AdminSubmissionsPageEnhanced = ({ setPage }) => {
  const { profile } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [successModal, setSuccessModal] = useState(null); // { businessName, tier, isPaid }

  useEffect(() => {
    loadSubmissions();
  }, [filter]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const { data } = await db.submissions.getAll(filter === 'all' ? null : filter);

      // For approved submissions, check if provider still exists
      if (data && data.length > 0) {
        const { data: providers } = await db.serviceProviders.getAll();
        const providerEmails = new Set((providers || []).map(p => p.email?.toLowerCase()));

        // Mark submissions where provider was deleted
        const enrichedData = data.map(sub => ({
          ...sub,
          providerDeleted: sub.status === 'approved' && !providerEmails.has(sub.contact_email?.toLowerCase())
        }));
        setSubmissions(enrichedData);
      } else {
        setSubmissions(data || []);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced approval that creates service provider + subscription
  const handleApprove = async (sub) => {
    // Get tier/logo from columns or fallback to additional_info parsing
    const subTier = getSubmissionTier(sub);
    const subLogo = getSubmissionLogo(sub);
    const subBillingPeriod = getSubmissionBillingPeriod(sub);

    // For paid tiers, check payment proof and confirm
    const isPaidTier = subTier && subTier !== 'free';
    const hasPaymentProof = sub.payment_proof_url;
    const paymentAmount = sub.payment_amount || (
      subBillingPeriod === 'annual'
        ? (TIER_CONFIG[subTier]?.price_yearly || 0)
        : (TIER_CONFIG[subTier]?.price_monthly || 0)
    );

    let confirmMessage;
    if (isPaidTier) {
      if (hasPaymentProof) {
        confirmMessage = `Approve "${sub.business_name}" (${subTier} tier)?\n\n` +
          `Payment Amount: ${formatCurrency(paymentAmount)}\n` +
          `Payment Method: ${sub.payment_method?.replace('_', ' ') || 'Not specified'}\n` +
          `${sub.payment_transaction_ref ? `Transaction Ref: ${sub.payment_transaction_ref}\n` : ''}` +
          `\nBy approving, you confirm that payment has been verified.\nThe listing will go LIVE immediately.`;
      } else {
        // Warn about missing payment proof
        confirmMessage = `WARNING: No payment proof uploaded!\n\n` +
          `Approve "${sub.business_name}" (${subTier} tier) anyway?\n\n` +
          `Amount Due: ${formatCurrency(paymentAmount)}\n\n` +
          `The listing will go LIVE immediately without payment verification.`;
      }
    } else {
      confirmMessage = `Approve "${sub.business_name}" and create free listing?`;
    }

    if (!window.confirm(confirmMessage)) return;

    setApproving(true);
    try {
      // 1. Create service provider record - ALL approved listings are ACTIVE
      const providerData = {
        type: sub.business_type === 'other' ? 'other' : sub.business_type,
        name: sub.business_name,
        contact_name: sub.contact_name,
        email: sub.contact_email,
        phone: sub.contact_phone,
        parish: sub.parish,
        address: sub.address,
        website: sub.website,
        description: sub.description,
        services: sub.services || [],
        business_hours: sub.business_hours || null,
        logo_url: subLogo,
        logo_position: sub.logo_position || { x: 50, y: 50, zoom: 1 },
        tier: subTier,
        is_active: true, // All approved listings are immediately active
        is_verified: false
      };

      const { data: provider, error: providerError } = await db.serviceProviders.create(providerData);
      if (providerError) throw providerError;

      // 2. Create subscription if not free tier
      if (subTier && subTier !== 'free') {
        // Use billing period selected by user, default to monthly
        await subscriptions.create(provider.id, subTier, subBillingPeriod, profile?.id);
      }

      // 3. Mark submission as approved and payment verified (for paid tiers)
      await db.submissions.approve(sub.id);

      // 4. Mark payment as verified if paid tier with proof
      if (isPaidTier && hasPaymentProof) {
        await db.submissions.verifyPayment(sub.id, profile?.id);
      }

      // Notify submitter
      adminNotifications.sendEmail({
        type: 'business_approved',
        title: 'Your listing has been approved',
        message: `${sub.business_name} is now live`,
        to_email: sub.contact_email,
        data: {
          business_name: sub.business_name,
          contact_name: sub.contact_name,
          tier: subTier,
          // Banking details are attached server-side from app_settings; we supply the amount
          amount: isPaidTier ? paymentAmount : 0,
          billing_period: subBillingPeriod
        }
      }).catch(err => console.warn('Approval email failed:', err));

      // Show success modal with next steps
      setSuccessModal({
        businessName: sub.business_name,
        tier: subTier,
        isPaid: isPaidTier,
        paymentVerified: isPaidTier && hasPaymentProof,
        email: sub.contact_email
      });
      loadSubmissions();
      setSelected(null);
    } catch (error) {
      console.error('Error approving:', error);
      alert('Error creating listing: ' + error.message);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this submission?')) return;
    try {
      const sub = submissions.find(s => s.id === id);
      await db.submissions.reject(id, rejectReason);
      if (sub?.contact_email) {
        adminNotifications.sendEmail({
          type: 'business_rejected',
          title: 'Update on your listing request',
          message: `Update regarding ${sub.business_name}`,
          to_email: sub.contact_email,
          data: {
            business_name: sub.business_name,
            contact_name: sub.contact_name,
            reason: rejectReason || null
          }
        }).catch(err => console.warn('Rejection email failed:', err));
      }
      loadSubmissions();
      setSelected(null);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this submission permanently?')) return;
    try {
      await db.submissions.delete(id);
      loadSubmissions();
      setSelected(null);
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-brand-100 text-brand-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Success Modal */}
      {successModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-brand-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Listing Created!</h3>
              <p className="text-gray-600 mb-4">
                <span className="font-semibold">{successModal.businessName}</span> has been approved.
              </p>

              {successModal.isPaid ? (
                successModal.paymentVerified ? (
                  // Paid tier with payment verified
                  <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-4">
                    <p className="text-brand-700 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      <span><strong>Payment verified!</strong> {successModal.tier} tier listing is now <strong>LIVE</strong>.</span>
                    </p>
                  </div>
                ) : (
                  // Paid tier without payment proof (approved anyway)
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <p className="text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      <span>{successModal.tier} tier listing is <strong>LIVE</strong> but payment was not verified.</span>
                    </p>
                    <p className="text-amber-600 text-sm mt-2">
                      Make sure to collect payment from the business owner.
                    </p>
                  </div>
                )
              ) : (
                <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-4">
                  <p className="text-brand-700 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Free tier listing is <strong>immediately active</strong> and visible on the site!</span>
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setSuccessModal(null); setPage('admin-service-providers'); }}
                  className="flex-1 px-4 py-3 bg-brand-600 text-white font-bold rounded-xl hover:bg-brand-700"
                >
                  View All Listings
                </button>
              </div>
              <button
                onClick={() => setSuccessModal(null)}
                className="mt-3 text-gray-500 hover:text-gray-700 text-sm"
              >
                Close and stay here
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <FileText className="w-8 h-8" />Business Submissions
            </h2>
            <p className="text-red-100 mt-1">Review and approve "Get Listed" requests</p>
          </div>
          <button onClick={loadSubmissions} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl font-medium capitalize ${filter === f ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Submissions</h3>
          <p className="text-gray-400 mt-1">No {filter !== 'all' && filter} submissions found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((sub) => {
            // Get tier/logo from columns or fallback to additional_info parsing
            const subTier = getSubmissionTier(sub);
            const subLogo = getSubmissionLogo(sub);
            const subBillingPeriod = getSubmissionBillingPeriod(sub);

            return (
            <div key={sub.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Logo Preview */}
                    {subLogo ? (
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                        <img
                          src={subLogo}
                          alt={sub.business_name}
                          className="w-full h-full object-cover"
                          style={{
                            objectPosition: sub.logo_position
                              ? `${sub.logo_position.x}% ${sub.logo_position.y}%`
                              : 'center'
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-8 h-8 text-gray-400" />
                      </div>
                    )}

                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-lg">{sub.business_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(sub.status)}`}>
                          {sub.status}
                        </span>
                        {sub.providerDeleted && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-bold flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Provider Deleted
                          </span>
                        )}
                        <TierBadge tier={subTier} />
                        {subBillingPeriod && subTier !== 'free' && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium capitalize">
                            {subBillingPeriod}
                          </span>
                        )}
                        {sub.banner_ad_interest && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium flex items-center gap-1">
                            <Megaphone className="w-3 h-3" /> Banner Ad
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 mt-1 capitalize">{sub.business_type}</p>
                      <p className="text-sm text-gray-400">{sub.contact_email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelected(selected === sub.id ? null : sub.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    {selected === sub.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>

                {selected === sub.id && (
                  <div className="mt-4 pt-4 border-t space-y-4">
                    {/* Contact Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Contact Name</p>
                        <p className="font-medium">{sub.contact_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <a href={`mailto:${sub.contact_email}`} className="font-medium text-blue-600 hover:underline">{sub.contact_email}</a>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="font-medium">{sub.contact_phone || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Parish</p>
                        <p className="font-medium">{sub.parish || 'N/A'}</p>
                      </div>
                      {sub.address && (
                        <div className="col-span-2">
                          <p className="text-sm text-gray-500">Address</p>
                          <p className="font-medium">{sub.address}</p>
                        </div>
                      )}
                      {sub.website && (
                        <div className="col-span-2">
                          <p className="text-sm text-gray-500">Website</p>
                          <a href={sub.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{sub.website}</a>
                        </div>
                      )}
                    </div>

                    {sub.description && (
                      <div>
                        <p className="text-sm text-gray-500">Description</p>
                        <p className="text-gray-700">{sub.description}</p>
                      </div>
                    )}

                    {sub.services?.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500">Services</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {sub.services.map((s, i) => (
                            <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                              {typeof s === 'object' ? (s.label || s.value || JSON.stringify(s)) : s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Billing Period Selected by User */}
                    {sub.billing_period && sub.tier !== 'free' && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-sm text-gray-500">Billing Period (selected by user)</p>
                        <p className="font-medium capitalize">{sub.billing_period}</p>
                      </div>
                    )}

                    {/* Banner Ad Interest */}
                    {sub.banner_ad_interest && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <p className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
                          <Megaphone className="w-4 h-4" /> Banner Ad Interest
                        </p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {sub.banner_ad_placement && (
                            <div>
                              <p className="text-gray-500">Placement</p>
                              <p className="font-medium capitalize">{sub.banner_ad_placement}</p>
                            </div>
                          )}
                          {sub.banner_ad_duration && (
                            <div>
                              <p className="text-gray-500">Duration</p>
                              <p className="font-medium">{sub.banner_ad_duration} months</p>
                            </div>
                          )}
                        </div>
                        {sub.banner_ad_notes && (
                          <div className="mt-2">
                            <p className="text-gray-500 text-sm">Notes</p>
                            <p className="text-amber-900">{sub.banner_ad_notes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Logo Preview Large */}
                    {sub.logo_url && (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Logo Preview</p>
                        <div className="w-32 h-32 rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200">
                          <img
                            src={sub.logo_url}
                            alt={sub.business_name}
                            className="w-full h-full object-cover"
                            style={{
                              objectPosition: sub.logo_position
                                ? `${sub.logo_position.x}% ${sub.logo_position.y}%`
                                : 'center'
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Payment Information Section - for paid tiers */}
                    {subTier && subTier !== 'free' && (
                      <div className={`rounded-xl p-4 ${
                        sub.payment_proof_url
                          ? 'bg-brand-50 border-2 border-brand-200'
                          : 'bg-red-50 border-2 border-red-200'
                      }`}>
                        <p className={`text-sm font-bold mb-3 flex items-center gap-2 ${
                          sub.payment_proof_url ? 'text-brand-800' : 'text-red-800'
                        }`}>
                          <DollarSign className="w-4 h-4" />
                          Payment Information
                          {sub.payment_proof_url ? (
                            <span className="ml-auto px-2 py-0.5 bg-brand-200 text-brand-800 rounded text-xs">Proof Uploaded</span>
                          ) : (
                            <span className="ml-auto px-2 py-0.5 bg-red-200 text-red-800 rounded text-xs">No Payment Proof</span>
                          )}
                        </p>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-gray-500">Amount Due</p>
                            <p className="font-bold text-lg">
                              {sub.payment_amount ? formatCurrency(sub.payment_amount) : (
                                subBillingPeriod === 'annual'
                                  ? formatCurrency(TIER_CONFIG[subTier]?.price_yearly || 0)
                                  : formatCurrency(TIER_CONFIG[subTier]?.price_monthly || 0)
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Payment Method</p>
                            <p className="font-medium capitalize">{sub.payment_method?.replace('_', ' ') || 'Not specified'}</p>
                          </div>
                          {sub.payment_transaction_ref && (
                            <div>
                              <p className="text-gray-500">Transaction Ref</p>
                              <p className="font-medium">{sub.payment_transaction_ref}</p>
                            </div>
                          )}
                          {sub.payment_date && (
                            <div>
                              <p className="text-gray-500">Payment Date</p>
                              <p className="font-medium">{new Date(sub.payment_date).toLocaleDateString()}</p>
                            </div>
                          )}
                        </div>

                        {/* Proof of Payment */}
                        {sub.payment_proof_url && (
                          <div className="mt-4 pt-3 border-t border-brand-200">
                            <p className="text-sm text-gray-500 mb-2">Proof of Payment</p>
                            <div className="flex items-center gap-3">
                              {sub.payment_proof_url.toLowerCase().endsWith('.pdf') ? (
                                <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center">
                                  <FileText className="w-8 h-8 text-red-600" />
                                </div>
                              ) : (
                                <img
                                  src={sub.payment_proof_url}
                                  alt="Payment proof"
                                  className="w-16 h-16 object-cover rounded-lg border"
                                />
                              )}
                              <div className="flex-1">
                                <p className="font-medium text-sm">{sub.payment_proof_filename || 'Payment proof'}</p>
                              </div>
                              <a
                                href={sub.payment_proof_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                              >
                                <ExternalLink className="w-4 h-4" />
                                View
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Payment Verified Badge */}
                        {sub.payment_verified && (
                          <div className="mt-3 flex items-center gap-2 text-brand-700">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">Payment Verified</span>
                            {sub.payment_verified_at && (
                              <span className="text-sm text-gray-500">
                                on {new Date(sub.payment_verified_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-gray-400">Submitted: {new Date(sub.created_at).toLocaleString()}</p>

                    {sub.status === 'pending' && (
                      <div className="pt-4 border-t space-y-4">
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleApprove(sub)}
                            disabled={approving}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl disabled:opacity-50"
                          >
                            {approving ? (
                              <RefreshCw className="w-5 h-5 animate-spin" />
                            ) : (
                              <CheckCircle className="w-5 h-5" />
                            )}
                            Approve & Create Listing
                          </button>
                          <button
                            onClick={() => handleReject(sub.id)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl"
                          >
                            <XCircle className="w-5 h-5" />Reject
                          </button>
                        </div>
                        <input
                          type="text"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Rejection reason (optional)"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:outline-none"
                        />
                      </div>
                    )}

                    {sub.status !== 'pending' && (
                      <div className="flex items-center gap-3 pt-4 border-t">
                        {/* Re-approve button for orphaned submissions */}
                        {sub.providerDeleted && (
                          <button
                            onClick={() => handleApprove(sub)}
                            disabled={approving}
                            className="flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-lg disabled:opacity-50"
                          >
                            {approving ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                            Re-approve
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(sub.id)}
                          className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium"
                        >
                          <Trash2 className="w-4 h-4" />Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== SERVICE PROVIDERS MANAGEMENT ====================
export const AdminServiceProvidersPage = ({ setPage }) => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const [tierFilter, setTierFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});

  const PARISHES = [
    'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
    'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
    'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
  ];

  const PROVIDER_TYPES = [
    { value: 'tutor', label: 'General Tutor' },
    { value: 'math_tutor', label: 'Math Tutor' },
    { value: 'science_tutor', label: 'Science Tutor' },
    { value: 'language_tutor', label: 'Language Tutor' },
    { value: 'test_prep', label: 'Exam Prep Centre' },
    { value: 'homework_helper', label: 'Homework Helper' },
    { value: 'consultant', label: 'Education Consultant' },
    { value: 'school_supplies', label: 'School Supplies' },
    { value: 'bookshop', label: 'Bookshop' },
    { value: 'uniforms', label: 'Uniform Supplier' },
    { value: 'private_school', label: 'Private School' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    setLoading(true);
    try {
      // Get all providers including inactive ones for admin view
      const { data, error } = await db.serviceProviders.getAll();
      // Also fetch inactive ones
      setProviders(data || []);
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editing === 'new') {
        await db.serviceProviders.create(formData);
      } else {
        await db.serviceProviders.update(editing, formData);
      }
      loadProviders();
      setEditing(null);
      setFormData({});
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service provider? This cannot be undone.')) return;
    try {
      await db.serviceProviders.delete(id);
      loadProviders();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const toggleActive = async (provider) => {
    try {
      await db.serviceProviders.update(provider.id, { is_active: !provider.is_active });
      loadProviders();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setFormData(item);
  };

  const startNew = () => {
    setEditing('new');
    setFormData({ type: 'other', tier: 'free', is_active: true });
  };

  const filteredProviders = providers.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.email?.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' ||
      (filter === 'active' && p.is_active) ||
      (filter === 'inactive' && !p.is_active);
    const matchesTier = tierFilter === 'all' || p.tier === tierFilter;
    return matchesSearch && matchesFilter && matchesTier;
  });

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-brand-600 to-brand-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <Store className="w-8 h-8" />Service Providers
            </h2>
            <p className="text-brand-100 mt-1">Manage tutors, exam prep centres, consultants & other education service providers</p>
          </div>
          <div className="flex gap-2">
            <button onClick={loadProviders} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
              <RefreshCw className="w-5 h-5" />
            </button>
            <button onClick={startNew} className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30">
              <Plus className="w-5 h-5" />Add New
            </button>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="bg-brand-600 text-white p-4 rounded-t-2xl">
              <h3 className="font-bold text-lg">{editing === 'new' ? 'Add Service Provider' : 'Edit Service Provider'}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={formData.type || 'other'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  >
                    {PROVIDER_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={formData.contact_name || ''}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
                  <select
                    value={formData.tier || 'free'}
                    onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parish</label>
                  <select
                    value={formData.parish || ''}
                    onChange={(e) => setFormData({ ...formData, parish: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  >
                    <option value="">Select Parish...</option>
                    {PARISHES.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={formData.website || ''}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={formData.address || ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                <input
                  type="url"
                  value={formData.logo_url || ''}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  placeholder="https://..."
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active ?? true}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300"
                  />
                  <span className="font-medium">Active (visible in directory)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_verified ?? false}
                    onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300"
                  />
                  <span className="font-medium">Verified</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSave}
                  className="flex-1 py-3 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl"
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditing(null); setFormData({}); }}
                  className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
        >
          <option value="all">All Tiers</option>
          <option value="free">Free</option>
          <option value="basic">Basic</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* Providers List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : filteredProviders.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Service Providers</h3>
          <p className="text-gray-400 mt-1">No providers match your filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredProviders.map((provider) => (
            <div key={provider.id} className={`bg-white rounded-xl shadow-lg p-5 ${!provider.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-start gap-4">
                {/* Logo */}
                {provider.logo_url ? (
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                    <img src={provider.logo_url} alt={provider.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Store className="w-7 h-7 text-gray-400" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-lg">{provider.name}</h3>
                    <TierBadge tier={provider.tier} />
                    {provider.is_verified && (
                      <span className="px-2 py-0.5 bg-brand-100 text-brand-700 text-xs font-medium rounded-full">Verified</span>
                    )}
                    {!provider.is_active && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">Inactive</span>
                    )}
                  </div>
                  <p className="text-gray-500 capitalize">{provider.type} • {provider.parish || 'No parish'}</p>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                    {provider.phone && (
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{provider.phone}</span>
                    )}
                    {provider.email && (
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{provider.email}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(provider)}
                    className={`p-2 rounded-lg ${provider.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-brand-600 hover:bg-brand-50'}`}
                    title={provider.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {provider.is_active ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => setPage('admin-subscriptions')}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Manage Subscription"
                  >
                    <CreditCard className="w-5 h-5" />
                  </button>
                  <button onClick={() => startEdit(provider)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                    <Edit className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleDelete(provider.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== SUBSCRIPTIONS MANAGEMENT ====================
export const AdminSubscriptionsPage = ({ setPage }) => {
  const { profile } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        subscriptions.getAll(filter === 'all' ? null : filter),
        subscriptions.getDashboardStats()
      ]);
      setSubs(subsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (sub) => {
    if (!window.confirm(`Suspend subscription for "${sub.vendor?.name}"? Their listing will be hidden.`)) return;
    try {
      await subscriptions.suspend(sub.id);
      loadData();
    } catch (error) {
      console.error('Error suspending:', error);
    }
  };

  const handleActivate = async (sub) => {
    if (!window.confirm(`Manually activate subscription for "${sub.vendor?.name}"?`)) return;
    try {
      await subscriptions.activate(sub.id, null, profile?.id);
      loadData();
    } catch (error) {
      console.error('Error activating:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <CreditCard className="w-8 h-8" />Subscriptions
            </h2>
            <p className="text-indigo-100 mt-1">Manage vendor subscriptions and billing</p>
          </div>
          <button onClick={loadData} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow border-l-4 border-brand-500">
            <p className="text-2xl font-black text-brand-600">{stats.active_basic + stats.active_premium}</p>
            <p className="text-gray-500 text-sm">Active Paid</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow border-l-4 border-amber-500">
            <p className="text-2xl font-black text-amber-600">{stats.pending_payment}</p>
            <p className="text-gray-500 text-sm">Pending Payment</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow border-l-4 border-orange-500">
            <p className="text-2xl font-black text-orange-600">{stats.in_grace_period}</p>
            <p className="text-gray-500 text-sm">Grace Period</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow border-l-4 border-green-500">
            <p className="text-2xl font-black text-green-600">{formatCurrency(stats.revenue_this_month)}</p>
            <p className="text-gray-500 text-sm">Revenue This Month</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'active', 'grace_period', 'suspended'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl font-medium capitalize ${
              filter === f ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border'
            }`}
          >
            {f.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Subscriptions List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : subs.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Subscriptions</h3>
          <p className="text-gray-400 mt-1">No subscriptions match your filter</p>
        </div>
      ) : (
        <div className="space-y-3">
          {subs.map((sub) => (
            <div key={sub.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {sub.vendor?.logo_url ? (
                      <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100">
                        <img src={sub.vendor.logo_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                        <Store className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-lg">{sub.vendor?.name || 'Unknown Vendor'}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <TierBadge tier={sub.tier} />
                        <StatusBadge status={sub.status} />
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelected(selected === sub.id ? null : sub.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    {selected === sub.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>

                {/* Basic Info */}
                <div className="mt-3 flex items-center gap-6 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {sub.billing_period === 'annual' ? 'Annual' : 'Monthly'} billing
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    {formatCurrency(sub.amount)}
                  </span>
                  {sub.current_period_end && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Expires: {new Date(sub.current_period_end).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Expanded Details */}
                {selected === sub.id && (
                  <div className="mt-4 pt-4 border-t space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Period Start</p>
                        <p className="font-medium">{sub.current_period_start ? new Date(sub.current_period_start).toLocaleDateString() : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Period End</p>
                        <p className="font-medium">{sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Grace Period Ends</p>
                        <p className="font-medium">{sub.grace_period_end ? new Date(sub.grace_period_end).toLocaleDateString() : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Email</p>
                        <p className="font-medium">{sub.vendor?.email || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => setPage(`admin-payments?vendor=${sub.vendor_id}`)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                      >
                        <Receipt className="w-4 h-4" />Record Payment
                      </button>
                      {sub.status === 'pending' || sub.status === 'suspended' ? (
                        <button
                          onClick={() => handleActivate(sub)}
                          className="flex items-center gap-2 px-4 py-2 bg-brand-100 text-brand-700 rounded-lg hover:bg-brand-200"
                        >
                          <CheckCircle className="w-4 h-4" />Activate
                        </button>
                      ) : sub.status === 'active' || sub.status === 'grace_period' ? (
                        <button
                          onClick={() => handleSuspend(sub)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                        >
                          <XCircle className="w-4 h-4" />Suspend
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== PAYMENTS MANAGEMENT ====================
export const AdminPaymentsPage = ({ setPage }) => {
  const { profile } = useAuth();
  const [allPayments, setAllPayments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [vendorSubscriptions, setVendorSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    vendor_id: '',
    amount: '',
    payment_method: 'manual',
    transaction_ref: '',
    notes: '',
    activate: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, providersRes, subsRes] = await Promise.all([
        payments.getAll(),
        db.serviceProviders.getAll(),
        subscriptions.getAll()
      ]);
      setAllPayments(paymentsRes.data || []);
      setProviders(providersRes.data || []);
      setVendorSubscriptions(subsRes.data || []);
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get the correct price based on vendor's subscription billing period
  const getVendorPrice = (vendor) => {
    if (!vendor) return 0;
    const tierConfig = TIER_CONFIG[vendor.tier];
    if (!tierConfig) return 0;

    // Find vendor's subscription to get billing period
    const vendorSub = vendorSubscriptions.find(s => s.vendor_id === vendor.id);
    const billingPeriod = vendorSub?.billing_period || 'monthly';

    return billingPeriod === 'annual' ? tierConfig.price_yearly : tierConfig.price_monthly;
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.vendor_id || !paymentForm.amount) {
      alert('Please select a vendor and enter amount');
      return;
    }

    setSubmitting(true);
    try {
      await payments.record(
        paymentForm.vendor_id,
        parseFloat(paymentForm.amount),
        paymentForm.payment_method,
        paymentForm.transaction_ref || null,
        paymentForm.notes || null,
        profile?.id,
        paymentForm.activate
      );
      alert('Payment recorded successfully!');
      setShowRecordForm(false);
      setPaymentForm({
        vendor_id: '',
        amount: '',
        payment_method: 'manual',
        transaction_ref: '',
        notes: '',
        activate: true
      });
      loadData();
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Error recording payment: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Get vendor subscription to auto-fill amount
  const selectedVendor = providers.find(p => p.id === paymentForm.vendor_id);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <DollarSign className="w-8 h-8" />Payment History
            </h2>
            <p className="text-green-100 mt-1">Record and track vendor payments</p>
          </div>
          <div className="flex gap-2">
            <button onClick={loadData} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowRecordForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30"
            >
              <Plus className="w-5 h-5" />Record Payment
            </button>
          </div>
        </div>
      </div>

      {/* Record Payment Modal */}
      {showRecordForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="bg-green-600 text-white p-4 rounded-t-2xl">
              <h3 className="font-bold text-lg">Record Payment</h3>
            </div>
            <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Vendor *</label>
                <select
                  value={paymentForm.vendor_id}
                  onChange={(e) => {
                    const vendor = providers.find(p => p.id === e.target.value);
                    setPaymentForm({
                      ...paymentForm,
                      vendor_id: e.target.value,
                      amount: getVendorPrice(vendor) || ''
                    });
                  }}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none"
                  required
                >
                  <option value="">Select vendor...</option>
                  {providers.filter(p => p.tier !== 'free').map(p => {
                    const sub = vendorSubscriptions.find(s => s.vendor_id === p.id);
                    const billingLabel = sub?.billing_period === 'annual' ? ' (Annual)' : ' (Monthly)';
                    return (
                      <option key={p.id} value={p.id}>{p.name} ({p.tier}{billingLabel})</option>
                    );
                  })}
                </select>
              </div>

              {selectedVendor && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-sm text-gray-600">
                    <strong>{selectedVendor.name}</strong> - {selectedVendor.tier} tier
                  </p>
                  <p className="text-xs text-gray-500">{selectedVendor.email}</p>
                  {(() => {
                    const sub = vendorSubscriptions.find(s => s.vendor_id === selectedVendor.id);
                    return sub?.billing_period && (
                      <p className="text-xs text-blue-600 mt-1 capitalize">
                        Billing: {sub.billing_period} - {formatCurrency(getVendorPrice(selectedVendor))}
                      </p>
                    );
                  })()}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (JMD) *</label>
                  <input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none"
                  >
                    <option value="manual">Manual/Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="paypal">PayPal</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
                <input
                  type="text"
                  value={paymentForm.transaction_ref}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transaction_ref: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none"
                  placeholder="Receipt #, PayPal ID, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none"
                  rows={2}
                  placeholder="Optional notes..."
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={paymentForm.activate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, activate: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300"
                />
                <span className="font-medium">Activate/extend subscription after recording</span>
              </label>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <RefreshCw className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  Record Payment
                </button>
                <button
                  type="button"
                  onClick={() => setShowRecordForm(false)}
                  className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payments List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : allPayments.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Payments</h3>
          <p className="text-gray-400 mt-1">No payment records found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Vendor</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Method</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Reference</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {allPayments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    {new Date(payment.payment_date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{payment.vendor?.name || 'Unknown'}</p>
                    <p className="text-xs text-gray-500">{payment.vendor?.email}</p>
                  </td>
                  <td className="px-4 py-3 font-bold text-green-600">
                    {formatCurrency(payment.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm capitalize">
                    {payment.payment_method.replace('_', ' ')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {payment.transaction_ref || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      payment.status === 'completed' ? 'bg-brand-100 text-brand-700' :
                      payment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {payment.status === 'completed' && payment.vendor && (
                      <button
                        onClick={() => {
                          const invoiceData = createInvoiceDataFromPayment(
                            payment,
                            payment.vendor,
                            { tier: payment.vendor.tier || 'basic', billing_period: 'monthly' }
                          );
                          downloadInvoice(invoiceData);
                        }}
                        className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                        title="Download Invoice"
                      >
                        <Download className="w-4 h-4" />
                        Invoice
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== EMAIL TEMPLATES MANAGEMENT ====================
export const AdminEmailTemplatesPage = ({ setPage }) => {
  const { profile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editingSettings, setEditingSettings] = useState(false);
  const [formData, setFormData] = useState({});
  const [settingsForm, setSettingsForm] = useState({});
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, bankName, accountName, accountNumber, bankBranch, supportEmail] = await Promise.all([
        emailTemplates.getAll(),
        appSettings.get('payment_bank_name'),
        appSettings.get('payment_account_name'),
        appSettings.get('payment_account_number'),
        appSettings.get('payment_bank_branch'),
        appSettings.get('support_email')
      ]);
      setTemplates(templatesRes.data || []);
      setPaymentSettings({
        bank_name: bankName.data ? JSON.parse(bankName.data) : '',
        account_name: accountName.data ? JSON.parse(accountName.data) : '',
        account_number: accountNumber.data ? JSON.parse(accountNumber.data) : '',
        bank_branch: bankBranch.data ? JSON.parse(bankBranch.data) : '',
        support_email: supportEmail.data ? JSON.parse(supportEmail.data) : ''
      });
      setSettingsForm({
        bank_name: bankName.data ? JSON.parse(bankName.data) : '',
        account_name: accountName.data ? JSON.parse(accountName.data) : '',
        account_number: accountNumber.data ? JSON.parse(accountNumber.data) : '',
        bank_branch: bankBranch.data ? JSON.parse(bankBranch.data) : '',
        support_email: supportEmail.data ? JSON.parse(supportEmail.data) : ''
      });
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      await emailTemplates.update(editing, {
        subject: formData.subject,
        body_html: formData.body_html,
        body_text: formData.body_text,
        updated_by: profile?.id
      });
      alert('Template saved successfully!');
      loadData();
      setEditing(null);
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        appSettings.set('payment_bank_name', JSON.stringify(settingsForm.bank_name)),
        appSettings.set('payment_account_name', JSON.stringify(settingsForm.account_name)),
        appSettings.set('payment_account_number', JSON.stringify(settingsForm.account_number)),
        appSettings.set('payment_bank_branch', JSON.stringify(settingsForm.bank_branch)),
        appSettings.set('support_email', JSON.stringify(settingsForm.support_email))
      ]);
      alert('Payment settings saved!');
      loadData();
      setEditingSettings(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (template) => {
    setEditing(template.id);
    setFormData({
      subject: template.subject,
      body_html: template.body_html,
      body_text: template.body_text
    });
    setPreviewMode(false);
  };

  // Sample data for preview
  const getSampleVariables = (templateKey) => {
    const base = {
      business_name: 'ABC Tutoring Services',
      contact_name: 'John Smith',
      support_email: paymentSettings.support_email || 'info@jamaicaschoolsguide.com',
      bank_name: paymentSettings.bank_name || 'National Commercial Bank',
      account_name: paymentSettings.account_name || 'Jamaica Schools Guide',
      account_number: paymentSettings.account_number || '123456789',
      bank_branch: paymentSettings.bank_branch || 'New Kingston',
      reference_code: 'JSG-2026-00123',
      resubmit_url: '#'
    };

    const extras = {
      listing_approved_payment_instructions: {
        tier_name: 'Basic',
        billing_period: 'Monthly',
        amount_due: 'J$3,500'
      },
      payment_received_confirmation: {
        amount_paid: 'J$3,500',
        tier_name: 'Basic',
        period_start: 'Jan 15, 2024',
        period_end: 'Feb 15, 2024',
        renewal_date: 'Feb 15, 2024'
      },
      renewal_reminder: {
        tier_name: 'Basic',
        days_remaining: '7',
        expiry_date: 'Jan 22, 2024',
        renewal_amount: 'J$3,500'
      },
      grace_period_warning: {
        suspension_date: 'Jan 29, 2024',
        amount_due: 'J$3,500'
      },
      submission_rejected: {
        rejection_reason: 'The business registration documents provided were incomplete. Please upload a clear copy of your business registration certificate and TRN.',
        resubmit_url: '/get-listed'
      },
      free_tier_upgrade_promo: {
        upgrade_url: '/get-listed'
      }
    };

    return { ...base, ...(extras[templateKey] || {}) };
  };

  const renderPreview = (template) => {
    const variables = getSampleVariables(template.template_key);
    const rendered = emailTemplates.render(
      { subject: formData.subject, body_html: formData.body_html, body_text: formData.body_text },
      variables
    );
    return rendered;
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <Mail className="w-8 h-8" />Email Templates
            </h2>
            <p className="text-purple-100 mt-1">Customize email notifications sent to vendors</p>
          </div>
          <button onClick={loadData} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Payment Settings Card */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-amber-500 text-white p-4 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <CreditCard className="w-5 h-5" />Payment Information (Used in Templates)
          </h3>
          <button
            onClick={() => setEditingSettings(!editingSettings)}
            className="px-3 py-1 bg-white/20 rounded-lg hover:bg-white/30 text-sm"
          >
            {editingSettings ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <div className="p-4">
          {editingSettings ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={settingsForm.bank_name}
                    onChange={(e) => setSettingsForm({ ...settingsForm, bank_name: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                  <input
                    type="text"
                    value={settingsForm.account_name}
                    onChange={(e) => setSettingsForm({ ...settingsForm, account_name: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                  <input
                    type="text"
                    value={settingsForm.account_number}
                    onChange={(e) => setSettingsForm({ ...settingsForm, account_number: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Branch</label>
                  <input
                    type="text"
                    value={settingsForm.bank_branch}
                    onChange={(e) => setSettingsForm({ ...settingsForm, bank_branch: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Support Email</label>
                <input
                  type="email"
                  value={settingsForm.support_email}
                  onChange={(e) => setSettingsForm({ ...settingsForm, support_email: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                />
              </div>
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Bank</p>
                <p className="font-medium">{paymentSettings.bank_name || 'Not set'}</p>
              </div>
              <div>
                <p className="text-gray-500">Account Name</p>
                <p className="font-medium">{paymentSettings.account_name || 'Not set'}</p>
              </div>
              <div>
                <p className="text-gray-500">Account #</p>
                <p className="font-medium">{paymentSettings.account_number || 'Not set'}</p>
              </div>
              <div>
                <p className="text-gray-500">Branch</p>
                <p className="font-medium">{paymentSettings.bank_branch || 'Not set'}</p>
              </div>
              <div>
                <p className="text-gray-500">Support Email</p>
                <p className="font-medium">{paymentSettings.support_email || 'Not set'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Template Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
              <h3 className="font-bold">Edit Template: {templates.find(t => t.id === editing)?.name}</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewMode(!previewMode)}
                  className={`px-3 py-1 rounded-lg text-sm ${previewMode ? 'bg-white text-purple-600' : 'bg-white/20'}`}
                >
                  {previewMode ? 'Edit' : 'Preview'}
                </button>
                <button onClick={() => setEditing(null)} className="p-1 hover:bg-white/20 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {previewMode ? (
                <div className="space-y-4">
                  <div className="bg-gray-100 rounded-xl p-4">
                    <p className="text-sm text-gray-500 mb-1">Subject:</p>
                    <p className="font-bold">{renderPreview(templates.find(t => t.id === editing)).subject}</p>
                  </div>
                  <div className="border rounded-xl overflow-hidden">
                    <div
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderPreview(templates.find(t => t.id === editing)).bodyHtml) }}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      HTML Body
                      <span className="text-gray-400 font-normal ml-2">
                        (Available variables: {templates.find(t => t.id === editing)?.variables?.join(', ')})
                      </span>
                    </label>
                    <textarea
                      value={formData.body_html}
                      onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none font-mono text-sm"
                      rows={20}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plain Text Version (for email clients that don't support HTML)</label>
                    <textarea
                      value={formData.body_text || ''}
                      onChange={(e) => setFormData({ ...formData, body_text: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none font-mono text-sm"
                      rows={10}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t p-4 flex gap-3">
              <button
                onClick={handleSaveTemplate}
                disabled={saving}
                className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                Save Template
              </button>
              <button
                onClick={() => setEditing(null)}
                className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => (
            <div key={template.id} className="bg-white rounded-xl shadow-lg p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{template.name}</h3>
                  <p className="text-gray-500 text-sm mt-1">{template.description}</p>
                  <p className="text-xs text-gray-400 mt-2">
                    Variables: {template.variables?.join(', ')}
                  </p>
                </div>
                <button
                  onClick={() => startEdit(template)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                >
                  <Edit className="w-4 h-4" />Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== PAYMENT REQUESTS MANAGEMENT ====================
export const AdminPaymentRequestsPage = ({ setPage }) => {
  const { profile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionForm, setActionForm] = useState({ action: '', notes: '', reason: '' });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await paymentRequests.getAll(filter === 'all' ? null : filter);
      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error loading payment requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      const { error } = await paymentRequests.approve(
        selectedRequest.id,
        actionForm.notes,
        profile?.id
      );
      if (error) throw error;
      alert('Payment request approved! Vendor subscription has been activated.');
      setSelectedRequest(null);
      setActionForm({ action: '', notes: '', reason: '' });
      loadRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Error approving request: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !actionForm.reason) {
      alert('Please provide a rejection reason.');
      return;
    }
    setProcessing(true);
    try {
      const { error } = await paymentRequests.reject(
        selectedRequest.id,
        actionForm.reason,
        profile?.id
      );
      if (error) throw error;
      alert('Payment request rejected. Vendor will be notified.');
      setSelectedRequest(null);
      setActionForm({ action: '', notes: '', reason: '' });
      loadRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error rejecting request: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-JM', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-JM', {
      style: 'currency', currency: 'JMD', minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <Upload className="w-8 h-8" />Payment Requests
            </h2>
            <p className="text-amber-100 mt-1">Review proof of payment submissions from vendors</p>
          </div>
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <span className="bg-white text-amber-600 px-4 py-2 rounded-full font-bold">
                {pendingCount} pending
              </span>
            )}
            <button onClick={loadRequests} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { key: 'pending', label: 'Pending', color: 'amber' },
          { key: 'approved', label: 'Approved', color: 'emerald' },
          { key: 'rejected', label: 'Rejected', color: 'red' },
          { key: 'all', label: 'All', color: 'gray' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === tab.key
                ? `bg-${tab.color}-500 text-white`
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <Upload className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No payment requests</h3>
          <p className="text-gray-400 mt-2">
            {filter === 'pending' ? 'No pending requests to review' : `No ${filter} requests found`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div key={request.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className={`p-4 ${
                request.status === 'pending' ? 'bg-amber-50 border-b-2 border-amber-200' :
                request.status === 'approved' ? 'bg-brand-50 border-b-2 border-brand-200' :
                'bg-red-50 border-b-2 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      request.status === 'pending' ? 'bg-amber-200' :
                      request.status === 'approved' ? 'bg-brand-200' :
                      'bg-red-200'
                    }`}>
                      {request.status === 'pending' ? <Clock className="w-6 h-6 text-amber-700" /> :
                       request.status === 'approved' ? <CheckCircle className="w-6 h-6 text-brand-700" /> :
                       <XCircle className="w-6 h-6 text-red-700" />}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{request.vendor?.name || 'Unknown Vendor'}</h3>
                      <p className="text-sm text-gray-500">
                        {request.request_type === 'reactivation' ? 'Reactivation Request' :
                         request.request_type === 'renewal' ? 'Renewal Payment' :
                         'Initial Payment'} • {formatDate(request.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black text-gray-900">{formatCurrency(request.amount)}</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      request.status === 'approved' ? 'bg-brand-100 text-brand-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Payment Method</p>
                    <p className="font-medium capitalize">{request.payment_method?.replace('_', ' ') || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Transaction Ref</p>
                    <p className="font-medium">{request.transaction_ref || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Payment Date</p>
                    <p className="font-medium">{formatDate(request.payment_date)}</p>
                  </div>
                </div>

                {/* Proof of Payment */}
                {request.proof_of_payment_url && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm font-medium text-blue-800 mb-2">Proof of Payment</p>
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-blue-600" />
                      <div className="flex-1">
                        <p className="font-medium">{request.proof_of_payment_filename || 'Payment proof'}</p>
                      </div>
                      <a
                        href={request.proof_of_payment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View
                      </a>
                    </div>
                  </div>
                )}

                {/* Vendor Contact */}
                <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                  {request.vendor?.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />{request.vendor.email}
                    </span>
                  )}
                  {request.vendor?.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />{request.vendor.phone}
                    </span>
                  )}
                </div>

                {/* Review Info (for processed requests) */}
                {request.status !== 'pending' && (
                  <div className="mt-4 pt-4 border-t text-sm">
                    <p className="text-gray-500">
                      {request.status === 'approved' ? 'Approved' : 'Rejected'} by {request.reviewed_by_user?.full_name || 'Admin'} on {formatDate(request.reviewed_at)}
                    </p>
                    {request.admin_notes && (
                      <p className="text-gray-600 mt-1"><strong>Notes:</strong> {request.admin_notes}</p>
                    )}
                    {request.rejection_reason && (
                      <p className="text-red-600 mt-1"><strong>Reason:</strong> {request.rejection_reason}</p>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                {request.status === 'pending' && (
                  <div className="mt-4 pt-4 border-t flex gap-3">
                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setActionForm({ action: 'approve', notes: '', reason: '' });
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Approve & Activate
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setActionForm({ action: 'reject', notes: '', reason: '' });
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-100 hover:bg-red-200 text-red-700 font-bold rounded-xl"
                    >
                      <XCircle className="w-5 h-5" />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className={`p-4 rounded-t-2xl ${
              actionForm.action === 'approve' ? 'bg-brand-600' : 'bg-red-600'
            } text-white`}>
              <h3 className="font-bold text-lg flex items-center gap-2">
                {actionForm.action === 'approve' ? (
                  <><CheckCircle className="w-6 h-6" />Approve Payment</>
                ) : (
                  <><XCircle className="w-6 h-6" />Reject Payment</>
                )}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-bold">{selectedRequest.vendor?.name}</p>
                <p className="text-sm text-gray-500">Amount: {formatCurrency(selectedRequest.amount)}</p>
              </div>

              {actionForm.action === 'approve' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes (optional)
                  </label>
                  <textarea
                    value={actionForm.notes}
                    onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    rows={3}
                    placeholder="Any notes about this approval..."
                  />
                  <p className="text-sm text-brand-600 mt-2">
                    This will record the payment and activate the vendor's subscription.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={actionForm.reason}
                    onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:outline-none"
                    rows={3}
                    placeholder="Explain why this payment is being rejected..."
                    required
                  />
                  <p className="text-sm text-red-600 mt-2">
                    The vendor will see this reason in their portal.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={actionForm.action === 'approve' ? handleApprove : handleReject}
                  disabled={processing || (actionForm.action === 'reject' && !actionForm.reason)}
                  className={`flex-1 py-3 font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 ${
                    actionForm.action === 'approve'
                      ? 'bg-brand-600 hover:bg-brand-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  {processing ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : actionForm.action === 'approve' ? (
                    <><CheckCircle className="w-5 h-5" />Confirm Approval</>
                  ) : (
                    <><XCircle className="w-5 h-5" />Confirm Rejection</>
                  )}
                </button>
                <button
                  onClick={() => {
                    setSelectedRequest(null);
                    setActionForm({ action: '', notes: '', reason: '' });
                  }}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== ADMIN PROVIDER REPORTS PAGE ====================
export const AdminProviderReportsPage = () => {
  const { profile } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadReports = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('service_provider_reports')
        .select('*, service_providers(name, email, is_active)')
        .order('created_at', { ascending: false });
      if (filter !== 'all') query = query.eq('status', filter);
      const { data } = await query;
      setReports(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReports(); }, [filter]);

  const updateStatus = async (id, status, extra = {}) => {
    setProcessing(true);
    try {
      await supabase.from('service_provider_reports').update({
        status,
        reviewed_by: profile?.id,
        reviewed_at: new Date().toISOString(),
        resolution_notes: resolveNotes || null,
        ...extra
      }).eq('id', id);
      loadReports();
      setSelected(null);
      setResolveNotes('');
    } finally {
      setProcessing(false);
    }
  };

  const deactivateProvider = async (providerId, report) => {
    setProcessing(true);
    try {
      await supabase.from('service_providers').update({ is_active: false }).eq('id', providerId);
      await updateStatus(report.id, 'resolved');
      // Notify the provider
      if (report.service_providers?.email) {
        adminNotifications.sendEmail({
          type: 'business_rejected',
          title: 'Your listing has been removed',
          message: `Your listing on Jamaica Schools Guide has been removed`,
          to_email: report.service_providers.email,
          data: {
            business_name: report.service_providers.name,
            contact_name: report.service_providers.name,
            reason: resolveNotes || 'Violation of our listing guidelines'
          }
        }).catch(() => {});
      }
    } finally {
      setProcessing(false);
    }
  };

  const REASON_LABELS = {
    fraud: 'Fraud / Scam',
    misrepresentation: 'Misrepresentation',
    inappropriate: 'Inappropriate Behaviour',
    spam: 'Spam / Fake Listing',
    other: 'Other'
  };

  const STATUS_COLORS = {
    pending: 'bg-amber-100 text-amber-700',
    reviewed: 'bg-blue-100 text-blue-700',
    resolved: 'bg-brand-100 text-brand-700',
    dismissed: 'bg-gray-100 text-gray-600'
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-black text-gray-900 mb-1">Provider Reports</h2>
      <p className="text-gray-500 mb-6">User-submitted reports about service providers</p>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['pending', 'reviewed', 'resolved', 'dismissed', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl font-semibold text-sm capitalize transition-colors ${
              filter === f ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><RefreshCw className="w-8 h-8 animate-spin text-brand-500" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-20 text-gray-400">No reports found</div>
      ) : (
        <div className="space-y-3">
          {reports.map(report => (
            <div
              key={report.id}
              className="bg-white border-2 border-gray-100 rounded-2xl p-5 cursor-pointer hover:border-brand-200 transition-colors"
              onClick={() => setSelected(selected?.id === report.id ? null : report)}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-gray-900">{report.service_providers?.name || 'Unknown Provider'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[report.status]}`}>
                      {report.status}
                    </span>
                    {!report.service_providers?.is_active && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Deactivated</span>
                    )}
                  </div>
                  <p className="text-sm text-red-600 font-medium">{REASON_LABELS[report.reason] || report.reason}</p>
                  {report.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{report.description}</p>}
                </div>
                <p className="text-xs text-gray-400 whitespace-nowrap">{new Date(report.created_at).toLocaleDateString()}</p>
              </div>

              {selected?.id === report.id && (
                <div className="mt-4 pt-4 border-t space-y-3" onClick={e => e.stopPropagation()}>
                  {report.reporter_email && (
                    <p className="text-sm text-gray-600">Reporter: <a href={`mailto:${report.reporter_email}`} className="text-brand-600">{report.reporter_email}</a></p>
                  )}
                  {report.description && (
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-xl">{report.description}</p>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Resolution notes</label>
                    <textarea
                      rows={2}
                      value={resolveNotes}
                      onChange={e => setResolveNotes(e.target.value)}
                      placeholder="Internal notes or reason for action…"
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:border-brand-400 focus:outline-none resize-none"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {report.status === 'pending' && (
                      <button onClick={() => updateStatus(report.id, 'reviewed')} disabled={processing}
                        className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl font-semibold text-sm hover:bg-blue-200 disabled:opacity-50">
                        Mark Reviewed
                      </button>
                    )}
                    <button onClick={() => updateStatus(report.id, 'resolved')} disabled={processing}
                      className="px-4 py-2 bg-brand-100 text-brand-700 rounded-xl font-semibold text-sm hover:bg-brand-200 disabled:opacity-50">
                      Resolve (keep listing)
                    </button>
                    {report.service_providers?.is_active && (
                      <button onClick={() => deactivateProvider(report.service_providers_id || report.provider_id, report)} disabled={processing}
                        className="px-4 py-2 bg-red-600 text-white rounded-xl font-semibold text-sm hover:bg-red-700 disabled:opacity-50">
                        Remove Listing + Notify
                      </button>
                    )}
                    <button onClick={() => updateStatus(report.id, 'dismissed')} disabled={processing}
                      className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-200 disabled:opacity-50">
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN PROVIDER REVIEWS PAGE ====================
const StarDisplay = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} className={`w-4 h-4 ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} />
    ))}
  </div>
);

export const AdminProviderReviewsPage = ({ setPage }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState(null);
  const [rejectNotes, setRejectNotes] = useState({});

  useEffect(() => { loadReviews(); }, [filter]);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const { data, error } = await db.reviews.getAll(filter === 'all' ? null : filter);
      if (error) throw error;
      setReviews(data || []);
    } catch (err) {
      console.error('Error loading reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, notes = null) => {
    setProcessing(id);
    try {
      const { error } = await db.reviews.updateStatus(id, status, notes);
      if (error) throw error;
      setReviews(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert('Error updating review: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const pendingCount = reviews.filter(r => r.status === 'pending').length;

  const STATUS_COLORS = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-brand-100 text-brand-700',
    rejected: 'bg-red-100 text-red-700',
  };

  const FILTERS = ['pending', 'approved', 'rejected', 'all'];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <Star className="w-8 h-8" />Provider Reviews
            </h2>
            <p className="text-amber-100 mt-1">Moderate submitted ratings and reviews</p>
          </div>
          <button onClick={() => setPage('admin')} className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30">
            <ArrowLeft className="w-5 h-5" />Back
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-xl font-medium capitalize transition-all text-sm relative ${filter === f ? 'bg-white text-amber-600 shadow-lg' : 'bg-white/20 hover:bg-white/30'}`}>
              {f}
              {f === 'pending' && pendingCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading reviews…</div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl shadow text-gray-400">
          No {filter === 'all' ? '' : filter} reviews found.
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map(review => (
            <div key={review.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-bold text-gray-900 text-lg">
                      {review.service_providers?.name || 'Unknown Provider'}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <StarDisplay rating={review.rating} />
                      <span className="text-sm font-semibold text-gray-600">{review.rating}/5</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[review.status] || 'bg-gray-100 text-gray-600'}`}>
                        {review.status}
                      </span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {new Date(review.created_at).toLocaleDateString('en-JM', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>

                {review.title && <p className="font-semibold text-gray-800 mb-1">{review.title}</p>}
                <p className="text-gray-600 text-sm leading-relaxed">{review.body}</p>
                <p className="text-xs text-gray-400 mt-2">— {review.reviewer_name}{review.reviewer_email ? ` (${review.reviewer_email})` : ''}</p>

                {review.admin_notes && (
                  <p className="mt-2 text-xs text-gray-500 italic border-t pt-2">Admin notes: {review.admin_notes}</p>
                )}
              </div>

              {review.status === 'pending' && (
                <div className="px-5 pb-4 space-y-3">
                  <textarea
                    placeholder="Rejection reason (optional)…"
                    value={rejectNotes[review.id] || ''}
                    onChange={e => setRejectNotes(prev => ({ ...prev, [review.id]: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 resize-none focus:ring-2 focus:ring-amber-300 focus:border-transparent"
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <button onClick={() => updateStatus(review.id, 'approved')} disabled={processing === review.id}
                      className="flex-1 px-4 py-2 bg-brand-600 text-white rounded-xl font-semibold text-sm hover:bg-brand-700 disabled:opacity-50 flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4" />Approve
                    </button>
                    <button onClick={() => updateStatus(review.id, 'rejected', rejectNotes[review.id] || null)} disabled={processing === review.id}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-semibold text-sm hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                      <XCircle className="w-4 h-4" />Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default {
  AdminSubmissionsPageEnhanced,
  AdminServiceProvidersPage,
  AdminSubscriptionsPage,
  AdminPaymentsPage,
  AdminEmailTemplatesPage,
  AdminPaymentRequestsPage,
  AdminProviderReportsPage,
  AdminProviderReviewsPage
};
